import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Button,
    Card,
    Tabs,
    Tab,
    CardContent,
    styled,
    createTheme,
    ThemeProvider,
    Grid,
} from '@mui/material';
import Footer from '../navbar/Footer';

// Création d'un thème personnalisé
const theme = createTheme({
    palette: {
        primary: {
            main: '#2E7D32',
            light: '#4CAF50',
            dark: '#1B5E20',
        },
        secondary: {
            main: '#388E3C',
        },
        background: {
            default: '#f9f9f9',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
});

// Style personnalisé pour les onglets
const StyledTabs = styled(Tabs)({
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '30px',
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.95rem',
        minWidth: 'auto',
        marginRight: '20px',
        color: '#666',
        '&.Mui-selected': {
            color: '#2E7D32',
            fontWeight: 600,
        },
    },
    '& .MuiTabs-indicator': {
        backgroundColor: '#2E7D32',
        height: '3px',
    },
});

// Style pour les cartes
const StyledCard = styled(Card)({
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        borderColor: '#4CAF50',
    },
    border: '1px solid #eaeaea',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
});

// Style pour le contenu des cartes
const StyledCardContent = styled(CardContent)({
    padding: '20px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
});

// Données pour les Projections Démographiques
const projectionsData = [
    {
        title: "Projections Démographiques : Projections par Ages Spécifiques",
        link: "/projectionAgeSpecifique",
        description: "Projections détaillées par âge individuel pour une analyse fine de l'évolution démographique",
    },
    {
        title: "Projections par Groupes d'Ages Quinquennaux",
        link: "/projectionGroupeAgeQuin",
        description: "Projections structurées par tranches d'âge de 5 ans pour les analyses sectorielles et la planification",
    },
    {
        title: "Projections Démographiques Globales",
        link: "/propDemographique",
        description: "Projections démographiques globales et tendances à long terme de la population burundaise",
    },
];

// Données pour les Enquêtes
const enquetesData = [
    {
        title: "Enquêtes Santé-Démographie",
        link: "/enquenteSanteDemographe",
        description: "Données complètes sur la santé reproductive, la mortalité maternelle et infantile, et les indicateurs démographiques clés",
    },
    {
        title: "Enquêtes Ménages",
        link: "/enqueteMenage",
        description: "Conditions de vie, dépenses des ménages, accès aux services de base et indicateurs socio-économiques",
    },
    {
        title: "Enquêtes Agricoles",
        link: "/enqueteAgricole",
        description: "Statistiques agricoles détaillées : production, superficies cultivées, pratiques culturales et productivité",
    },
    {
        title: "Enquêtes Nutrition",
        link: "/enqueteNutrition",
        description: "État nutritionnel de la population, sécurité alimentaire et pratiques alimentaires",
    },
    {
        title: "Enquêtes Commerce, Transport et Entreprises",
        link: "/enqueteCommerceTransportEntreprise",
        description: "Données sur les échanges commerciaux, transport de marchandises et performances des entreprises",
    },
];

// Données pour les Recensements
const recensementData = [
    {
        title: "Résultats du RGPHAE 2024",
        link: "/resultatRGPHAE2024",
        description: "Résultats préliminaires complets du Recensement Général de la Population, de l'Habitat, de l'Agriculture et de l'Elevage de 2024",
    },
    {
        title: "Recensement Général de la Population et de l'Habitat 2008",
        link: "/recensement",
        description: "Données détaillées du recensement de 2008 avec analyses démographiques, sociales et économiques",
    },
    {
        title: "Recensement Général des Cafetiers 2006-2007",
        link: "/RecensementCafe",
        description: "Inventaire complet des exploitations caféières, producteurs et statistiques de production",
    },
];

// Données pour les Autres Rapports
const autresData = [
    {
        title: "Recueil des concepts, définitions, nomenclatures et méthodologies",
        link: "/documents/rapports/Recueil_des_concepts_définitions_nomenclatures_du_SSN_du_Burundi_VF_05_12_2024.pdf",
        description: "Document de référence pour un langage statistique unifié au sein du Système Statistique National burundais",
        year: 2024,
    },
    {
        title: "Rapport d'Évaluation par les Pairs du Système Statistique National",
        link: "/documents/rapports/RapportfinalSSNburundais.pdf",
        description: "Évaluation complète du SSN burundais selon les standards de la Charte Africaine de la Statistique",
        year: 2024,
    },
    {
        title: "Évaluation par les pairs du SSN - Communiqué de presse",
        link: "/documents/rapports/EvaluationparLespairsduSSNduBurundi_Communiquedepresse.docx",
        description: "Communiqué officiel présentant les conclusions de l'évaluation par les pairs du système statistique",
        year: 2024,
    },
    {
        title: "Stratégie Nationale de Développement de la Statistique 2022-2026",
        link: "/documents/rapports/17082022-SNDS-III-Burundi-Rapport-synthese.pdf",
        description: "Feuille de route stratégique pour le développement du système statistique national",
        year: 2022,
    },
    {
        title: "Schéma Directeur Informatique du SSN et de l'ISTEEBU 2016-2020",
        link: "/documents/rapports/3.-SCHEMA-DIRECTEUR-INFORMATIQUE-rapport-final.pdf",
        description: "Plan directeur pour la modernisation des systèmes d'information statistiques",
        year: 2015,
    },
    {
        title: "Rapport National sur la Cartographie des Indicateurs ODD",
        link: "/documents/rapports/1.-Rapport-National-Cartographie-et-niveaux-de-référence-des-indicateurs-des-objectifs-de-développement-durable.pdf",
        description: "Cartographie complète des indicateurs ODD pour le suivi des objectifs de développement durable",
        year: 2018,
    },
    {
        title: "Governance, Peace and Security Statistics in Burundi",
        link: "/documents/rapports/4.-Governance-Peace-and-Security-Statistics-in-Burundi.pdf",
        description: "Statistiques sur la gouvernance, la paix et la sécurité pour une prise de décision éclairée",
        year: 2018,
    },
    {
        title: "Relevé des Conclusions et Recommandations CTIS 19ème Réunion",
        link: "/documents/rapports/CTIS_19eme-Reunion_Releve-des-conclusions-et-Recommandations.pdf",
        description: "Synthèse des conclusions et recommandations du Comité Technique Interministériel de la Statistique",
        year: 2020,
    },
    {
        title: "Deuxième Stratégie Nationale de Développement de la Statistique",
        link: "/documents/rapports/NSDS-Burundi_Document-Principal-2.pdf",
        description: "Stratégie précédente pour le développement du système statistique national",
        year: 2016,
    },
];

const Rapport = () => {
    const [projectionsVisibleCount, setProjectionsVisibleCount] = useState(6);
    const [enquetesVisibleCount, setEnquetesVisibleCount] = useState(6);
    const [recensementVisibleCount, setRecensementVisibleCount] = useState(6);
    const [autresVisibleCount, setAutresVisibleCount] = useState(6);
    
    const [filterYearAutres, setFilterYearAutres] = useState('all');
    
    const [activeTab, setActiveTab] = useState(0);

    const handleChangeTab = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleLoadMoreProjections = () => {
        setProjectionsVisibleCount(prevCount => prevCount + 6);
    };

    const handleLoadMoreEnquetes = () => {
        setEnquetesVisibleCount(prevCount => prevCount + 6);
    };

    const handleLoadMoreRecensement = () => {
        setRecensementVisibleCount(prevCount => prevCount + 6);
    };

    const handleLoadMoreAutres = () => {
        setAutresVisibleCount(prevCount => prevCount + 6);
    };

    const handleFilterChangeAutres = (event) => {
        setFilterYearAutres(event.target.value);
        setAutresVisibleCount(6);
    };

    const getYearOptions = () => {
        const years = [...new Set(autresData.map(item => item.year))];
        years.sort((a, b) => b - a);
        return years;
    };

    const filterAutresData = () => {
        if (filterYearAutres === 'all') return autresData;
        return autresData.filter(item => item.year === parseInt(filterYearAutres, 10));
    };

    const menuItemStyle = {
        color: '#2E7D32',
    };

    const renderDocumentsGrid = (data, visibleCount, showYear = false, isFileDownload = false) => {
        const visibleData = data.slice(0, visibleCount);

        return (
            <>
                <Grid container spacing={3}>
                    {visibleData.map((document, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <StyledCard>
                                <StyledCardContent>
                                    <Typography variant="subtitle1" component="div" style={{ 
                                        color: theme.palette.primary.dark, 
                                        fontWeight: 500, 
                                        flex: 1,
                                        fontSize: '1rem',
                                        marginBottom: '12px'
                                    }}>
                                        {document.title}
                                    </Typography>
                                    
                                    <Typography variant="body2" color="textSecondary" paragraph style={{ 
                                        marginBottom: '16px', 
                                        color: '#666',
                                        fontSize: '0.875rem',
                                        flex: 1
                                    }}>
                                        {document.description}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                        {showYear && document.year && (
                                            <Typography variant="caption" color="textSecondary" style={{ color: '#888', fontSize: '0.75rem' }}>
                                                Publié en {document.year}
                                            </Typography>
                                        )}
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            href={document.link}
                                            target={isFileDownload ? "_blank" : "_self"}
                                            rel={isFileDownload ? "noopener noreferrer" : ""}
                                            size="small"
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                },
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                padding: '6px 16px',
                                                marginLeft: 'auto'
                                            }}
                                        >
                                            {isFileDownload ? "Télécharger le rapport" : "Accéder à la page"}
                                        </Button>
                                    </Box>
                                </StyledCardContent>
                            </StyledCard>
                        </Grid>
                    ))}
                </Grid>

                {visibleCount < data.length && (
                    <Box display="flex" justifyContent="center" style={{ marginTop: '30px' }}>
                        <Button
                            onClick={activeTab === 0 ? handleLoadMoreProjections : 
                                    activeTab === 1 ? handleLoadMoreEnquetes : 
                                    activeTab === 2 ? handleLoadMoreRecensement : 
                                    handleLoadMoreAutres}
                            variant="outlined"
                            sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    borderColor: theme.palette.primary.dark,
                                    backgroundColor: 'rgba(46, 125, 50, 0.04)',
                                },
                                textTransform: 'none',
                                padding: '8px 24px',
                            }}
                        >
                            Afficher plus de rapports
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <div style={{ backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
                    <Typography variant="h5" gutterBottom style={{ 
                        color: theme.palette.primary.dark, 
                        marginBottom: '8px',
                        fontWeight: 600 
                    }}>
                        Centre de Documentation
                    </Typography>
                    <Typography variant="body1" style={{ color: '#666', marginBottom: '30px' }}>
                        Accédez à l'ensemble des publications statistiques, rapports techniques, enquêtes et recensements produits par l'INSBU
                    </Typography>

                    <div style={{ marginBottom: '30px' }}>
                        <StyledTabs
                            value={activeTab}
                            onChange={handleChangeTab}
                            textColor="primary"
                            indicatorColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Projections Démographiques" />
                            <Tab label="Enquêtes" />
                            <Tab label="Recensements" />
                            <Tab label="Autres Rapports" />
                        </StyledTabs>
                    </div>

                    {/* Section Projections Démographiques */}
                    {activeTab === 0 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Projections Démographiques
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Les projections démographiques fournissent des estimations futures de la population par âge, sexe et caractéristiques démographiques. 
                                Ces données sont essentielles pour la planification du développement, l'allocation des ressources et l'élaboration de politiques sociales et économiques.
                            </Typography>

                            {renderDocumentsGrid(projectionsData, projectionsVisibleCount, false, false)}
                        </>
                    )}

                    {/* Section Enquêtes */}
                    {activeTab === 1 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Enquêtes Statistiques
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Les enquêtes statistiques couvrent divers domaines socio-économiques et fournissent des données précieuses pour l'analyse et la prise de décision. 
                                Ces enquêtes permettent de suivre les indicateurs clés de développement et d'évaluer l'impact des politiques publiques.
                            </Typography>

                            {renderDocumentsGrid(enquetesData, enquetesVisibleCount, false, false)}
                        </>
                    )}

                    {/* Section Recensements */}
                    {activeTab === 2 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Recensements
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Les recensements fournissent une photographie détaillée de la population et de l'habitat à un moment donné. 
                                Ils constituent la base essentielle pour la planification du développement, l'allocation des ressources 
                                et la formulation de politiques publiques adaptées aux besoins réels de la population.
                            </Typography>

                            {renderDocumentsGrid(recensementData, recensementVisibleCount, false, false)}
                        </>
                    )}

                    {/* Section Autres Rapports */}
                    {activeTab === 3 && (
                        <>
                            <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
                                Autres Rapports
                            </Typography>
                            <Typography variant="body1" paragraph style={{ marginBottom: '24px', color: '#555' }}>
                                Cette section regroupe divers rapports techniques, études méthodologiques, évaluations stratégiques 
                                et documents de référence produits par l'INSBU et ses partenaires pour le développement du système statistique national.
                            </Typography>

                            <FormControl fullWidth margin="normal" style={{ marginBottom: '30px', width: '320px' }}>
                                <InputLabel id="year-filter-autres-label" style={{ color: '#666' }}>
                                    Filtrer par année
                                </InputLabel>
                                <Select
                                    labelId="year-filter-autres-label"
                                    id="year-filter-autres"
                                    value={filterYearAutres}
                                    label="Filtrer par année"
                                    onChange={handleFilterChangeAutres}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#4CAF50',
                                        },
                                    }}
                                >
                                    <MenuItem value="all" style={menuItemStyle}>
                                        Tous les rapports
                                    </MenuItem>
                                    {getYearOptions().map(year => (
                                        <MenuItem key={year} value={year} style={menuItemStyle}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {renderDocumentsGrid(filterAutresData(), autresVisibleCount, true, true)}
                        </>
                    )}
                </Container>
                <Footer />
            </div>
        </ThemeProvider>
    );
};

export default Rapport;